import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp, apiFetch } from "../../context/AppContext";
import {
  Lightbulb,
  Calendar,
  Route,
  AlertTriangle,
  FileText,
  Handshake,
  Shield,
  CheckCircle2,
  Zap,
  ChevronRight,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { buildComplianceRecommendations, formatDeadlineDate } from "../../data/complianceRoadmap";
import { DPDP_POLICY_COUNT } from "../../data/dpdpPolicies";
import { formatTokenCount, getTokensRemaining } from "../../utils/policyTokenUtils";
import PolicyGenerateModal from "./PolicyGenerateModal";

const RECOMMENDATION_ROUTE_MODULE = {
  "/user/gap-assessment": "gap",
  "/user/pia": "dpia",
  "/user/ropa": "ropa",
  "/user/tprm": "tprm",
  "/user/breach-management": "breach",
  "/user/reports": "reports",
  "/user/policy-store": "policy_store",
  "/user/asset-tracker": "asset_tracker",
};

function canOpenRecommendationRoute(route, allowedModules) {
  const mod = RECOMMENDATION_ROUTE_MODULE[route];
  if (!mod) return true;
  return allowedModules.includes(mod);
}

const ICON_MAP = {
  calendar: Calendar,
  route: Route,
  alert: AlertTriangle,
  document: FileText,
  handshake: Handshake,
  shield: Shield,
  check: CheckCircle2,
  zap: Zap,
};

const PRIORITY_STYLE = {
  critical: {
    card: "border-red-200 bg-gradient-to-br from-red-50/90 to-orange-50/70",
    pill: "bg-red-100 text-red-700 border-red-200",
    icon: "text-red-600",
    label: "Urgent",
  },
  high: {
    card: "border-amber-200 bg-gradient-to-br from-amber-50/90 to-yellow-50/60",
    pill: "bg-amber-100 text-amber-800 border-amber-200",
    icon: "text-amber-600",
    label: "High priority",
  },
  medium: {
    card: "border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-sky-50/50",
    pill: "bg-indigo-100 text-indigo-700 border-indigo-200",
    icon: "text-indigo-600",
    label: "Recommended",
  },
  low: {
    card: "border-slate-200 bg-white/80",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    icon: "text-slate-500",
    label: "Info",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

function RecommendationCard({ rec, index, onNavigate }) {
  const style = PRIORITY_STYLE[rec.priority] || PRIORITY_STYLE.medium;
  const Icon = ICON_MAP[rec.icon] || Lightbulb;

  return (
    <motion.article
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className={`rounded-2xl border p-4 flex flex-col gap-2 transition-shadow hover:shadow-md ${style.card}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon size={16} className={`flex-shrink-0 mt-0.5 ${style.icon}`} />
          <h3 className="text-sm font-bold text-slate-900 leading-snug">{rec.title}</h3>
        </div>
        <span className={`text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border flex-shrink-0 ${style.pill}`}>
          {style.label}
        </span>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed pl-6">{rec.body}</p>
      {rec.route && (
        <button
          type="button"
          onClick={() => onNavigate(rec.route)}
          className="self-start ml-6 flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          {rec.action}
          <ChevronRight size={13} />
        </button>
      )}
    </motion.article>
  );
}

function PolicyRecommendationCard({ orgName, tokensRemaining, onGenerate, policyStoreEnabled }) {
  return (
    <motion.article
      custom={0}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/95 to-teal-50/70 p-4 flex flex-col gap-3 h-full transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <ShieldCheck size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
          <h3 className="text-sm font-bold text-slate-900 leading-snug">
            Adopt DPDP-aligned policy framework
          </h3>
        </div>
        <span className="text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border flex-shrink-0 bg-emerald-100 text-emerald-700 border-emerald-200">
          Policies
        </span>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed pl-6">
        Generate {DPDP_POLICY_COUNT} recommended policies under the Digital Personal Data Protection Act, 2023 —
        tailored for <span className="font-medium text-slate-800">{orgName}</span>. AI drafts are saved
        to Policy Store as PDFs before the {formatDeadlineDate()} deadline.
      </p>

      <p className="text-[10px] font-semibold text-emerald-800 pl-6 tabular-nums">
        {formatTokenCount(tokensRemaining)} tokens remaining · use tokens wisely
      </p>

      <ul className="text-[10px] text-slate-500 space-y-1 pl-6 list-disc marker:text-emerald-400">
        <li>Personal data protection & privacy notice</li>
        <li>Consent, DSAR & breach notification</li>
        <li>Vendor, cross-border & children&apos;s data</li>
      </ul>

      <button
        type="button"
        onClick={policyStoreEnabled ? onGenerate : undefined}
        disabled={!policyStoreEnabled}
        title={
          policyStoreEnabled
            ? undefined
            : "Policy Store is not enabled for your organisation"
        }
        className={`self-start ml-6 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors shadow-sm ${
          policyStoreEnabled
            ? "text-white bg-emerald-600 hover:bg-emerald-700 cursor-pointer"
            : "text-slate-400 bg-slate-200 cursor-not-allowed"
        }`}
      >
        <FileText size={13} />
        Generate Policy
        <ChevronRight size={13} />
      </button>
      {!policyStoreEnabled && (
        <p className="text-[10px] text-slate-500 pl-6">
          Policy Store is not included in your organisation&apos;s license.
        </p>
      )}
    </motion.article>
  );
}

export default function ComplianceRecommendationsPanel({
  progress = {},
  orgName = "Your organisation",
  loading = false,
}) {
  const navigate = useNavigate();
  const { currentUser, showNotification } = useApp();
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const userKey = currentUser?.id ?? currentUser?.email ?? orgName ?? "default";
  const [tokensRemaining, setTokensRemaining] = useState(() => getTokensRemaining(userKey));
  const allowedModules = currentUser?.modules || [];
  const recommendations = loading
    ? []
    : buildComplianceRecommendations({ progress, orgName });
  const policyStoreEnabled = (currentUser?.modules || []).includes("policy_store");

  useEffect(() => {
    let cancelled = false;
    apiFetch("/policy-store/ai-tokens")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && Number.isFinite(Number(data.remaining))) {
          setTokensRemaining(Math.max(0, Number(data.remaining)));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userKey]);

  const handleRecommendationNavigate = (route) => {
    if (!canOpenRecommendationRoute(route, allowedModules)) return;
    navigate(route);
  };

  const openPolicyModal = () => {
    setPolicyModalOpen(true);
  };

  const closePolicyModal = () => {
    setPolicyModalOpen(false);
    apiFetch("/policy-store/ai-tokens")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && Number.isFinite(Number(data.remaining))) {
          setTokensRemaining(Math.max(0, Number(data.remaining)));
        }
      })
      .catch(() => {});
  };

  return (
    <div className="px-3 sm:px-5 py-4 sm:py-5">
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
            boxShadow: "0 4px 14px rgba(245,158,11,0.35)",
          }}
        >
          <Lightbulb size={18} className="text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-0.5">
            <Sparkles size={11} />
            Smart recommendations
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 font-display">
            Stay on track for {formatDeadlineDate()}
          </h2>
          <p className="text-xs text-slate-600 mt-0.5 max-w-xl">
            Personalised next steps based on your module progress and the DPDP compliance deadline.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-3 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-28 rounded-2xl bg-slate-100" />
            ))}
          </div>
          <div className="h-40 rounded-2xl bg-slate-100" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-3 items-start">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
            {recommendations.map((rec, i) => (
              <RecommendationCard
                key={rec.id}
                rec={rec}
                index={i}
                onNavigate={handleRecommendationNavigate}
              />
            ))}
          </div>

          <PolicyRecommendationCard
            orgName={orgName}
            tokensRemaining={tokensRemaining}
            onGenerate={openPolicyModal}
            policyStoreEnabled={policyStoreEnabled}
          />
        </div>
      )}

      <PolicyGenerateModal
        open={policyModalOpen}
        onClose={closePolicyModal}
        organizationName={orgName}
        userKey={userKey}
        onSuccess={(data) => {
          showNotification(data?.message || "Policies saved to Policy Store", "success");
          navigate("/user/policy-store");
        }}
      />
    </div>
  );
}
